/**
 * App shell — v.1-D §7.22 + §7.25 + Prompt 11.10.
 *
 * Hosts the four chrome components used by `AppLayout`:
 *   - Sidebar              — primary nav with badge counts and Profile section.
 *   - Header               — breadcrumb row + global actions (notifications, ⌘K).
 *   - NotificationsDrawer  — slide-over for the alert feed.
 *   - CommandPalette       — ⌘K command bar.
 *
 * The SidebarProfileSection (§7.25 / Prompt 11.10) is appended to the bottom
 * of Sidebar. It reads from `useCurrentMember()` per Decision D-003 — NEVER
 * from the JWT — and routes the row click to `/settings` (sign-out happens
 * inside Settings, not directly from the sidebar, to avoid mis-clicks).
 *
 * The chrome bodies below are intentionally minimal: this is the v.1-D shell
 * scaffold. Per-surface enrichment (mockup-fidelity nav decoration, drawer
 * animation, fuzzy-match command index) lands incrementally in §13 walkthroughs
 * and the §16 phase plan. The contract the tests pin is the export surface
 * and the Profile section's data wiring — not the visual chrome.
 */
import type { JSX, ReactNode } from 'react';
import { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { I } from './icons';
import { Pill, type PillTone } from './primitives/Pill';
import { Btn } from './primitives/Btn';
import { useCurrentMember } from '../hooks/useCurrentMember';

// DevOps 2026-05-26: SidebarProfileSection removed — the mockup-port Sidebar
// below inlines the footer profile chrome (matches the mockup pattern).
// useCurrentMember + navigate('/settings') wiring preserved inline.

// ---------------------------------------------------------------------------
// Sidebar — primary navigation. The badge fields are sourced by AppLayout via
// `useNavBadges()` and threaded down as props so the Sidebar stays a pure
// presentation surface.
// ---------------------------------------------------------------------------

export interface SidebarProps {
  active: string;
  onNavigate: (id: string) => void;
  alertsCount: number;
  channelsLive: number;
  pendingAutomations: number;
}

// DevOps 2026-05-26: ported from mockup
// (docs/mockups/circa-frontend-v2/circa-front-end-25-mayo-2026/project/app-shell.jsx
//  lines 9-44). The v1-D PR shipped a flat NAV_ITEMS scaffold per the
// "intentionally minimal" comment at the top of this file. The 6-section
// structure + headings + per-item badge tone are the design contract.
interface NavBadge {
  tone: PillTone;
  text: string;
  dot?: boolean;
}

interface NavItem {
  id: string;
  label: string;
  icon: keyof typeof I;
}

interface NavSection {
  heading: string;
  items: readonly NavItem[];
}

const NAV_SECTIONS: readonly NavSection[] = [
  {
    heading: 'Home',
    items: [{ id: 'dashboard', label: 'Dashboard', icon: 'home' }],
  },
  {
    heading: 'Broadcast',
    items: [
      { id: 'channels', label: 'Channels', icon: 'tv' },
      { id: 'library', label: 'Content library', icon: 'library' },
      { id: 'schedules', label: 'Schedules', icon: 'calendar' },
      { id: 'guide', label: 'Programme guide', icon: 'guide' },
    ],
  },
  {
    heading: 'Monetize',
    items: [
      { id: 'compliance', label: 'Compliance', icon: 'shield' },
      { id: 'batch', label: 'Bulk operations', icon: 'layers' },
      { id: 'editorial', label: 'Editorial inbox', icon: 'inbox' },
    ],
  },
  {
    heading: 'Insights',
    items: [
      { id: 'analytics', label: 'Analytics', icon: 'bar' },
      { id: 'quality', label: 'Viewer quality', icon: 'gauge' },
      { id: 'cost', label: 'Cost', icon: 'dollar' },
      { id: 'uptime', label: 'Uptime', icon: 'uptime' },
      { id: 'spend', label: 'Provider spend', icon: 'network' },
      { id: 'player', label: 'Player health', icon: 'player' },
    ],
  },
  {
    heading: 'Operations',
    items: [
      { id: 'monitor', label: 'Live monitor', icon: 'pulse' },
      { id: 'health', label: 'System health', icon: 'server' },
      { id: 'automations', label: 'Automations', icon: 'bot' },
      { id: 'versions', label: 'Version history', icon: 'history' },
      { id: 'alerts', label: 'Alerts', icon: 'bell' },
    ],
  },
  {
    heading: 'Settings',
    items: [
      { id: 'settings', label: 'Settings', icon: 'settings' },
      { id: 'help', label: 'Help & support', icon: 'help' },
    ],
  },
];

/** Compute the badge for a nav item (mockup port — Sidebar render shell). */
function badgeFor(itemId: string, props: SidebarProps): NavBadge | null {
  if (itemId === 'channels' && props.channelsLive > 0) {
    return { tone: 'live', text: `${props.channelsLive} live`, dot: true };
  }
  if (itemId === 'alerts' && props.alertsCount > 0) {
    return { tone: 'bad', text: String(props.alertsCount) };
  }
  if (itemId === 'editorial' && props.pendingAutomations > 0) {
    return { tone: 'info', text: String(props.pendingAutomations) };
  }
  return null;
}

export function Sidebar(props: SidebarProps): JSX.Element {
  const member = useCurrentMember();
  const navigate = useNavigate();

  const profileInitials = member?.name
    ? member.name.split(' ').map((p) => p[0] ?? '').slice(0, 2).join('').toUpperCase()
    : member?.email?.slice(0, 2).toUpperCase() ?? '';

  return (
    <aside
      style={{
        width: 244,
        flexShrink: 0,
        height: '100vh',
        position: 'sticky',
        top: 0,
        borderRight: '1px solid var(--lightest-gray)',
        background: '#fcfcfd',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden',
      }}
    >
      {/* Wordmark → links to Dashboard */}
      <Link
        to="/"
        aria-label="Go to dashboard"
        style={{
          padding: '24px 24px 18px',
          display: 'flex',
          alignItems: 'baseline',
          gap: 8,
          textDecoration: 'none',
          color: 'inherit',
        }}
      >
        <span
          style={{
            fontFamily: 'var(--font-display)',
            fontSize: 26,
            fontWeight: 400,
            letterSpacing: -0.5,
            color: 'var(--ink)',
          }}
        >
          Circa
        </span>
        <span
          aria-hidden
          style={{
            width: 5,
            height: 5,
            borderRadius: 999,
            background: 'var(--live)',
            animation: 'circa-pulse var(--duration-pulse) ease-in-out infinite',
            alignSelf: 'center',
          }}
        />
      </Link>

      {/* Workspace switcher → settings/organization */}
      <button
        type="button"
        onClick={() => navigate('/settings/organization')}
        title="Workspace settings"
        style={{
          margin: '0 12px 14px',
          padding: '8px 12px',
          display: 'flex',
          alignItems: 'center',
          gap: 10,
          background: '#fff',
          border: '1px solid var(--lightest-gray)',
          borderRadius: 10,
          textAlign: 'left',
          cursor: 'pointer',
        }}
      >
        <span
          aria-hidden
          style={{
            width: 26,
            height: 26,
            borderRadius: 6,
            background: '#17171c',
            color: '#fff',
            display: 'inline-flex',
            alignItems: 'center',
            justifyContent: 'center',
            fontSize: 12,
            fontWeight: 600,
            fontFamily: 'var(--font-display)',
          }}
        >
          F
        </span>
        <span style={{ flex: 1, minWidth: 0 }}>
          <span style={{ display: 'block', fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>
            Fireclip Media
          </span>
          <span style={{ display: 'block', fontSize: 11, color: 'var(--muted)' }}>
            Workspace · Pro
          </span>
        </span>
        <I.chevD size={14} stroke="var(--muted)" />
      </button>

      {/* Nav */}
      <nav style={{ flex: 1, overflowY: 'auto', padding: '0 8px 16px' }}>
        {NAV_SECTIONS.map((section) => (
          <div key={section.heading} style={{ marginBottom: 18 }}>
            <div
              style={{
                padding: '6px 14px 8px',
                fontFamily: 'var(--font-mono)',
                textTransform: 'uppercase',
                fontSize: 10.5,
                letterSpacing: 0.7,
                color: 'var(--muted)',
              }}
            >
              {section.heading}
            </div>
            {section.items.map((item) => {
              const isActive = props.active === item.id;
              const IconComp = I[item.icon];
              const badge = badgeFor(item.id, props);
              return (
                <button
                  key={item.id}
                  type="button"
                  onClick={() => props.onNavigate(item.id)}
                  data-active={isActive ? 'true' : 'false'}
                  style={{
                    width: '100%',
                    display: 'flex',
                    alignItems: 'center',
                    gap: 10,
                    padding: '8px 12px',
                    borderRadius: 8,
                    marginBottom: 1,
                    background: isActive ? '#eef0f4' : 'transparent',
                    color: isActive ? 'var(--ink)' : '#3a3a44',
                    fontSize: 14,
                    fontWeight: isActive ? 500 : 400,
                    textAlign: 'left',
                    cursor: 'pointer',
                    transition: 'background 120ms',
                    border: 'none',
                  }}
                  onMouseEnter={(e) => {
                    if (!isActive) e.currentTarget.style.background = '#f4f5f7';
                  }}
                  onMouseLeave={(e) => {
                    if (!isActive) e.currentTarget.style.background = 'transparent';
                  }}
                >
                  <IconComp size={16} stroke={isActive ? 'var(--ink)' : '#5a5a64'} />
                  <span style={{ flex: 1 }}>{item.label}</span>
                  {badge && (
                    <Pill tone={badge.tone} size="sm" dot={badge.dot}>
                      {badge.text}
                    </Pill>
                  )}
                </button>
              );
            })}
          </div>
        ))}
      </nav>

      {/* Footer: profile (member-driven; mockup uses hardcoded "Marie Vasquez") */}
      {member && (
        <div
          style={{
            padding: '12px 12px 16px',
            borderTop: '1px solid var(--lightest-gray)',
            display: 'flex',
            alignItems: 'center',
            gap: 10,
          }}
        >
          <span
            aria-hidden
            style={{
              width: 32,
              height: 32,
              borderRadius: '50%',
              background: 'linear-gradient(135deg, #5b1d99, #1863dc)',
              color: '#fff',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              fontSize: 12,
              fontWeight: 600,
            }}
          >
            {profileInitials}
          </span>
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: 13, fontWeight: 500, color: 'var(--ink)' }}>
              {member.name ?? member.email}
            </div>
            <div style={{ fontSize: 11, color: 'var(--muted)' }}>{member.email}</div>
          </div>
          <button
            type="button"
            onClick={() => navigate('/settings')}
            title="Open settings"
            style={{
              padding: 6,
              borderRadius: 6,
              color: 'var(--muted)',
              display: 'inline-flex',
              alignItems: 'center',
              background: 'none',
              border: 'none',
              cursor: 'pointer',
            }}
          >
            <I.chevR size={14} />
          </button>
        </div>
      )}
    </aside>
  );
}

// ---------------------------------------------------------------------------
// Header — breadcrumb row + global actions.
// ---------------------------------------------------------------------------

export interface BreadcrumbEntry {
  label: string;
  /** When set, the segment renders as a Link to this path. */
  path?: string;
}

export interface HeaderProps {
  /**
   * Accepts either string labels (legacy) or {label, path} entries. When
   * a `path` is present, that segment becomes a clickable Link.
   */
  breadcrumbs: ReadonlyArray<string | BreadcrumbEntry>;
  onOpenNotifications: () => void;
  onOpenCmd: () => void;
  alertsCount: number;
}

// DevOps 2026-05-26: ported from mockup
// (docs/mockups/circa-frontend-v2/circa-front-end-25-mayo-2026/project/app-shell.jsx
//  lines 150-213). The v1-D PR shipped a minimal Header scaffold per the
// "intentionally minimal" comment at the top of this file. The mockup's
// chrome — sticky 85%-white blur, breadcrumb chevrons, 340px pill search
// trigger, Help ghost button, notification bell with red badge, "New
// channel" primary button — is the design contract.
export function Header(props: HeaderProps): JSX.Element {
  return (
    <header
      style={{
        position: 'sticky',
        top: 0,
        zIndex: 20,
        background: 'rgba(255,255,255,0.85)',
        backdropFilter: 'saturate(180%) blur(12px)',
        WebkitBackdropFilter: 'saturate(180%) blur(12px)',
        borderBottom: '1px solid var(--lightest-gray)',
        padding: '12px 32px',
        display: 'flex',
        alignItems: 'center',
        gap: 16,
      }}
    >
      {/* Breadcrumbs */}
      <nav
        aria-label="Breadcrumb"
        style={{ display: 'flex', alignItems: 'center', gap: 6, flex: 1 }}
      >
        {props.breadcrumbs.map((c, idx) => {
          const entry: BreadcrumbEntry =
            typeof c === 'string' ? { label: c } : c;
          const isLast = idx === props.breadcrumbs.length - 1;
          const labelStyle = {
            fontSize: 13,
            color: isLast ? 'var(--ink)' : 'var(--muted)',
            fontWeight: isLast ? 500 : 400,
            textDecoration: 'none',
          } as const;
          return (
            <span
              key={`${entry.label}-${idx}`}
              style={{ display: 'flex', alignItems: 'center', gap: 6 }}
            >
              {idx > 0 && <I.chevR size={12} stroke="var(--muted)" />}
              {entry.path && !isLast ? (
                <Link to={entry.path} style={labelStyle}>
                  {entry.label}
                </Link>
              ) : (
                <span style={labelStyle}>{entry.label}</span>
              )}
            </span>
          );
        })}
      </nav>

      {/* Search trigger */}
      <button
        type="button"
        onClick={props.onOpenCmd}
        aria-label="Open command palette"
        style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 10,
          background: '#f4f5f7',
          border: '1px solid transparent',
          padding: '7px 12px',
          borderRadius: 9999,
          color: 'var(--muted)',
          fontSize: 13,
          width: 340,
          cursor: 'pointer',
          transition: 'border-color 160ms',
        }}
        onMouseEnter={(e) => {
          e.currentTarget.style.borderColor = 'var(--border-cool)';
        }}
        onMouseLeave={(e) => {
          e.currentTarget.style.borderColor = 'transparent';
        }}
      >
        <I.search size={14} />
        <span style={{ flex: 1, textAlign: 'left' }}>
          Search channels, content, settings…
        </span>
        <span
          style={{
            fontFamily: 'var(--font-mono)',
            fontSize: 10,
            letterSpacing: 0.4,
            background: '#fff',
            border: '1px solid var(--border-cool)',
            padding: '2px 6px',
            borderRadius: 4,
          }}
        >
          ⌘K
        </span>
      </button>

      <Btn variant="ghost" size="sm" icon={<I.help size={16} />} href="/help">
        Help
      </Btn>

      <button
        type="button"
        onClick={props.onOpenNotifications}
        aria-label="Open notifications"
        style={{
          position: 'relative',
          padding: 8,
          borderRadius: 8,
          color: 'var(--ink)',
          display: 'inline-flex',
          alignItems: 'center',
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
        }}
      >
        <I.bell size={18} />
        {props.alertsCount > 0 && (
          <span
            aria-hidden
            style={{
              position: 'absolute',
              top: 4,
              right: 4,
              background: 'var(--bad)',
              color: '#fff',
              fontSize: 10,
              fontWeight: 600,
              padding: '1px 5px',
              borderRadius: 999,
              lineHeight: 1.2,
              fontVariantNumeric: 'tabular-nums',
            }}
          >
            {props.alertsCount}
          </span>
        )}
      </button>

      <Btn
        variant="primary"
        size="sm"
        icon={<I.plus size={14} />}
        href="/channels/new"
      >
        New channel
      </Btn>
    </header>
  );
}

// ---------------------------------------------------------------------------
// NotificationsDrawer — slide-over for alerts.
//
// DevOps 2026-05-26: ported from mockup
// (docs/mockups/circa-frontend-v2/circa-front-end-25-mayo-2026/project/app-shell.jsx
//  lines 216-270). 420px right-anchored slide-over with 20%-opacity scrim,
// unread-count subtitle, tone-dot per item (bad/warn → red/yellow, default
// → cta), hover row tint, "Mark all read" + "Notification preferences"
// footer buttons. AppLayout currently passes no items → empty list renders;
// matches mockup behavior.
// ---------------------------------------------------------------------------

export interface NotificationItem {
  tone?: 'bad' | 'warn' | 'info' | 'neutral';
  title: string;
  body: string;
  time: string;
}

export interface NotificationsDrawerProps {
  open: boolean;
  onClose: () => void;
  items?: ReadonlyArray<NotificationItem>;
}

export function NotificationsDrawer(props: NotificationsDrawerProps): JSX.Element | null {
  if (!props.open) return null;
  const items = props.items ?? [];
  return (
    <>
      <div
        onClick={props.onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(0,0,0,0.20)',
          zIndex: 80,
        }}
      />
      <aside
        role="dialog"
        aria-modal="true"
        aria-label="Notifications"
        style={{
          position: 'fixed',
          top: 0,
          right: 0,
          height: '100vh',
          width: 420,
          background: '#fff',
          zIndex: 81,
          boxShadow: '-12px 0 32px rgba(0,0,0,0.10)',
          display: 'flex',
          flexDirection: 'column',
          animation: 'circa-fade-in 200ms var(--ease-emphasized) both',
        }}
      >
        <header
          style={{
            padding: '20px 24px',
            borderBottom: '1px solid var(--lightest-gray)',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
          }}
        >
          <div>
            <h2
              style={{
                margin: 0,
                fontFamily: 'var(--font-display)',
                fontSize: 24,
                fontWeight: 400,
                letterSpacing: -0.4,
              }}
            >
              Notifications
            </h2>
            <p style={{ margin: '4px 0 0', fontSize: 13, color: 'var(--muted)' }}>
              {items.length} unread
            </p>
          </div>
          <button
            type="button"
            onClick={props.onClose}
            aria-label="Close notifications"
            style={{
              padding: 6,
              background: 'transparent',
              border: 'none',
              cursor: 'pointer',
              color: 'var(--ink)',
            }}
          >
            <I.x size={18} />
          </button>
        </header>
        <div style={{ flex: 1, overflowY: 'auto', padding: 8 }}>
          {items.map((n, i) => (
            <div
              key={i}
              style={{
                padding: '14px 16px',
                borderRadius: 10,
                display: 'flex',
                gap: 12,
                cursor: 'pointer',
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.background = '#f7f7f9';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.background = 'transparent';
              }}
            >
              <span
                aria-hidden
                style={{
                  width: 8,
                  height: 8,
                  borderRadius: 999,
                  marginTop: 7,
                  background:
                    n.tone === 'bad'
                      ? 'var(--bad)'
                      : n.tone === 'warn'
                        ? 'var(--warn)'
                        : 'var(--cta)',
                  flexShrink: 0,
                }}
              />
              <div style={{ flex: 1, minWidth: 0 }}>
                <div
                  style={{
                    fontSize: 14,
                    color: 'var(--ink)',
                    fontWeight: 500,
                    marginBottom: 2,
                  }}
                >
                  {n.title}
                </div>
                <div style={{ fontSize: 13, color: 'var(--muted)', lineHeight: 1.4 }}>
                  {n.body}
                </div>
                <div
                  style={{
                    fontFamily: 'var(--font-mono)',
                    textTransform: 'uppercase',
                    fontSize: 10,
                    color: 'var(--muted)',
                    letterSpacing: 0.5,
                    marginTop: 6,
                  }}
                >
                  {n.time}
                </div>
              </div>
            </div>
          ))}
        </div>
        <footer
          style={{
            padding: 16,
            borderTop: '1px solid var(--lightest-gray)',
            display: 'flex',
            gap: 8,
          }}
        >
          <Btn variant="secondary" size="sm" fullWidth>
            Mark all read
          </Btn>
          <Btn variant="ghost" size="sm" fullWidth>
            Notification preferences
          </Btn>
        </footer>
      </aside>
    </>
  );
}

// ---------------------------------------------------------------------------
// CommandPalette — ⌘K command surface.
//
// DevOps 2026-05-26: ported from mockup
// (docs/mockups/circa-frontend-v2/circa-front-end-25-mayo-2026/project/app-shell.jsx
//  lines 273-331). 580px centered modal at 15vh, search header with ESC hint,
// "Quick actions" mono-uppercase heading, icon + label + right-aligned hint
// items, hover row tint. Mockup hardcodes 7 quick actions with hint chips
// ("Broadcast", "Library", "Operations", etc.). The mockup's input is
// display-only; we wire a label-filter on top (keeps the visual chrome but
// makes typing functional). Each entry carries a `path` so AppLayout's
// onNavigate(path) signature is preserved.
// ---------------------------------------------------------------------------

export interface CommandPaletteProps {
  open: boolean;
  onClose: () => void;
  onNavigate: (path: string) => void;
}

interface PaletteEntry {
  path: string;
  label: string;
  hint: string;
  icon: keyof typeof I;
}

const PALETTE_ENTRIES: ReadonlyArray<PaletteEntry> = [
  { path: '/channels',    label: 'Channels',                  hint: 'Broadcast',  icon: 'tv' },
  { path: '/library',     label: 'Upload content',            hint: 'Library',    icon: 'upload' },
  { path: '/schedules',   label: 'Build a schedule',          hint: 'Schedules',  icon: 'calendar' },
  { path: '/monitor',     label: 'Live monitor',              hint: 'Operations', icon: 'pulse' },
  { path: '/alerts',      label: 'Active alerts',             hint: 'Operations', icon: 'bell' },
  { path: '/analytics',   label: 'Analytics',                 hint: 'Insights',   icon: 'bar' },
  { path: '/settings',    label: 'Notification preferences',  hint: 'Settings',   icon: 'settings' },
];

export function CommandPalette(props: CommandPaletteProps): JSX.Element | null {
  const [query, setQuery] = useState('');

  useEffect(() => {
    if (!props.open) setQuery('');
  }, [props.open]);

  if (!props.open) return null;

  const q = query.trim().toLowerCase();
  const results =
    q.length === 0
      ? PALETTE_ENTRIES
      : PALETTE_ENTRIES.filter(
          (entry) =>
            entry.label.toLowerCase().includes(q) ||
            entry.hint.toLowerCase().includes(q),
        );

  return (
    <>
      <div
        onClick={props.onClose}
        style={{
          position: 'fixed',
          inset: 0,
          background: 'rgba(20,10,40,0.45)',
          zIndex: 90,
        }}
      />
      <div
        role="dialog"
        aria-modal="true"
        aria-label="Command palette"
        style={{
          position: 'fixed',
          top: '15vh',
          left: '50%',
          transform: 'translateX(-50%)',
          width: 580,
          maxWidth: '90vw',
          background: '#fff',
          borderRadius: 16,
          boxShadow: '0 30px 80px rgba(0,0,0,0.25)',
          zIndex: 91,
          overflow: 'hidden',
          animation: 'circa-fade-in 180ms var(--ease-emphasized) both',
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: 10,
            padding: '16px 20px',
            borderBottom: '1px solid var(--lightest-gray)',
          }}
        >
          <I.search size={18} stroke="var(--muted)" />
          <input
            autoFocus
            placeholder="Type a command or search…"
            value={query}
            onChange={(e) => setQuery(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Escape') props.onClose();
            }}
            style={{
              flex: 1,
              border: 0,
              outline: 'none',
              background: 'transparent',
              fontSize: 15,
              color: 'var(--ink)',
            }}
          />
          <span
            style={{
              fontFamily: 'var(--font-mono)',
              fontSize: 10,
              color: 'var(--muted)',
              border: '1px solid var(--border-cool)',
              borderRadius: 4,
              padding: '2px 6px',
            }}
          >
            ESC
          </span>
        </div>
        <div style={{ padding: 8, maxHeight: 380, overflowY: 'auto' }}>
          <div
            style={{
              padding: '8px 12px',
              fontFamily: 'var(--font-mono)',
              textTransform: 'uppercase',
              fontSize: 10.5,
              letterSpacing: 0.7,
              color: 'var(--muted)',
            }}
          >
            Quick actions
          </div>
          {results.map((entry) => {
            const IconComp = I[entry.icon];
            return (
              <button
                key={entry.path}
                type="button"
                onClick={() => {
                  props.onNavigate(entry.path);
                  props.onClose();
                }}
                style={{
                  width: '100%',
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                  padding: '10px 12px',
                  borderRadius: 8,
                  textAlign: 'left',
                  background: 'transparent',
                  border: 'none',
                  cursor: 'pointer',
                  color: 'var(--ink)',
                }}
                onMouseEnter={(e) => {
                  e.currentTarget.style.background = '#f4f5f7';
                }}
                onMouseLeave={(e) => {
                  e.currentTarget.style.background = 'transparent';
                }}
              >
                <IconComp size={16} stroke="#5a5a64" />
                <span style={{ flex: 1, fontSize: 14 }}>{entry.label}</span>
                <span style={{ fontSize: 11, color: 'var(--muted)' }}>{entry.hint}</span>
              </button>
            );
          })}
          {results.length === 0 && (
            <div
              style={{
                padding: '12px 12px',
                fontSize: 12,
                color: 'var(--muted)',
              }}
            >
              No matches.
            </div>
          )}
        </div>
      </div>
    </>
  );
}

// Suppress an unused-ReactNode import warning when the toolchain is strict.
export type { ReactNode };
